import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/react-query/query-keys";
import { getWeatherPresentation } from "@/lib/weather/presentation";
import type { WeatherForecastResponse } from "@/lib/weather/types";

async function fetchStayWeather(
  city: string,
  checkIn: string,
  checkOut: string,
  signal: AbortSignal,
): Promise<WeatherForecastResponse> {
  const params = new URLSearchParams({ city, checkIn });
  if (checkOut) params.set("checkOut", checkOut);

  const res = await fetch(`/api/weather?${params}`, { signal });
  if (!res.ok) return { days: [], cityName: city };
  return (await res.json()) as WeatherForecastResponse;
}

export function useWeatherQuery(
  city: string,
  checkIn: string,
  checkOut: string,
) {
  const cityVal = city.trim() || undefined;
  const checkInVal = checkIn.trim() || undefined;
  const checkOutVal = checkOut.trim() || undefined;

  return useQuery({
    queryKey: queryKeys.search.weather(
      cityVal ?? "",
      checkInVal ?? "",
      checkOutVal ?? "",
    ),
    enabled: !!cityVal && !!checkInVal,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async ({ signal }) => {
      if (!cityVal || !checkInVal) return { days: [], cityName: "" };

      const data = await fetchStayWeather(
        cityVal,
        checkInVal,
        checkOutVal || checkInVal,
        signal,
      );

      const days = (data.days ?? []).map((day) => {
        const map = getWeatherPresentation(day.weatherCode);
        return {
          date: day.date,
          tempMax: day.tempMax,
          tempMin: day.tempMin,
          icon: map.icon,
          description: map.description,
          className: map.className,
        };
      });

      return { days, cityName: data.cityName || cityVal };
    },
  });
}
